import React, { useState, useMemo } from 'react';

const DataTable = ({
    columns,
    data,
    isLoading = false,
    onRowClick,
    emptyMessage = "Kayıt bulunamadı",
    pagination = true,
    initialPageSize = 10
}) => {
    const [sortConfig, setSortConfig] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(initialPageSize);

    // Sorting Logic
    const sortedData = useMemo(() => {
        if (!sortConfig) return data;

        return [...data].sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });
    }, [data, sortConfig]);

    // Pagination Logic
    const paginatedData = useMemo(() => {
        if (!pagination) return sortedData;

        const firstPageIndex = (currentPage - 1) * pageSize;
        const lastPageIndex = firstPageIndex + pageSize;
        return sortedData.slice(firstPageIndex, lastPageIndex);
    }, [sortedData, currentPage, pageSize, pagination]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    if (isLoading) {
        return (
            <div className="w-full p-8 text-center text-gray-400">
                <div className="animate-pulse flex flex-col items-center">
                    <div className="h-4 w-3/4 bg-gray-700 mb-4 rounded"></div>
                    <div className="h-4 w-1/2 bg-gray-700 rounded"></div>
                </div>
                <span className="mt-4 block">Yükleniyor...</span>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="w-full overflow-hidden rounded-xl border border-gray-700/50 bg-[#1e1e2e]/50 backdrop-blur-sm shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-gray-700/50 bg-[#181825]/80 text-gray-400 text-sm uppercase tracking-wider">
                                {columns.map((col, index) => (
                                    <th
                                        key={index}
                                        className="p-4 font-semibold"
                                        style={{ width: col.width }}
                                    >
                                        {col.header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td colSpan={columns.length} className="p-12 text-center text-gray-500 italic">
                                    {emptyMessage}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="border-b border-white/10 text-gray-400 text-sm uppercase tracking-wider" style={{ background: 'rgba(0,0,0,0.05)' }}>
                            {columns.map((col, index) => (
                                <th
                                    key={index}
                                    className={`font-semibold ${col.sortable ? 'cursor-pointer hover:text-white transition-colors' : ''}`}
                                    onClick={() => col.sortable && requestSort(col.key)}
                                    style={{
                                        width: col.width,
                                        padding: '16px 24px',
                                        color: 'var(--text-secondary)'
                                    }}
                                >
                                    <div className="flex items-center gap-2">
                                        {col.header}
                                        {sortConfig && sortConfig.key === col.key && (
                                            <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5 text-sm" style={{ color: 'var(--text-primary)' }}>
                        {paginatedData.map((row, rowIndex) => (
                            <tr
                                key={row.id || rowIndex}
                                onClick={() => onRowClick && onRowClick(row)}
                                className={`
                                    transition-colors duration-200 
                                    ${onRowClick ? 'cursor-pointer hover:bg-white/5' : 'hover:bg-white/5'}
                                `}
                            >
                                {columns.map((col, colIndex) => {
                                    const value = col.render ? col.render(row) :
                                        (typeof col.accessor === 'function' ? col.accessor(row) :
                                            (row[col.accessor] || row[col.key] || '-'));

                                    return (
                                        <td key={colIndex} className="whitespace-nowrap" style={{ padding: '16px 24px', ...col.cellStyle?.(row) }}>
                                            {value}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            {pagination && data.length > pageSize && (
                <div className="flex items-center justify-between p-4 border-t border-gray-700/50 bg-[#181825]/50">
                    <span className="text-sm text-gray-500">
                        Toplam {data.length} kayıttan {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, data.length)} arası gösteriliyor
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 rounded border border-gray-700 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Önceki
                        </button>
                        <button
                            onClick={() => setCurrentPage(prev => (prev * pageSize < data.length ? prev + 1 : prev))}
                            disabled={currentPage * pageSize >= data.length}
                            className="px-3 py-1 rounded border border-gray-700 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Sonraki
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataTable;
